import React, { useMemo } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { generateHref } from 'Helmet/GoustoHelmet'
import Helmet from 'react-helmet'
import { useDispatch } from 'react-redux'
import useSWR from 'swr'

import { Homepage } from '@features/homepage'
import { fromAPIRecipe, menuFetcherV3, Recipe } from '@library/menu-service'
import { Menu } from '@library/menu-service/queries/menuApiMenu/types'

import { homeGetStarted } from 'actions/home'
import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import routesConfig from 'config/routes'
import { useIsDiscountComprehensionEnabled } from 'hooks/useIsDiscountComprehensionEnabled'

import { useGetCtaProps } from './Cta/ctaHooks'
import {
  useCanShowPromoBanner,
  useGetPromoBannerDispatchedActions,
} from './PromoBanner/promoBannerHooks'
import { knownVariants, defaultVariant, seo } from './homeConfig'
import { usePromoBannerText } from './usePromoBannerText'

const formatRecipes = (response?: Menu): Recipe[] => {
  if (!response || !response.recipes) {
    return []
  }

  return Object.values(response.recipes).map((recipe) => fromAPIRecipe(recipe))
}

const getHelmetLink = (variant: string) => {
  const {
    client: { home },
  } = routesConfig
  const knownVariant = knownVariants.includes(variant) ? variant : defaultVariant
  const link = knownVariant !== 'default' ? [{ rel: 'canonical', href: generateHref(home) }] : []

  return link
}

type HomePageProps = { location: { query: { variant: string } } }

export const HomePageContent = ({ location }: HomePageProps) => {
  const dispatch = useDispatch()

  const { data: menuResponse } = useSWR(
    {
      numPortions: 2,
    },
    menuFetcherV3,
  )

  const link = useMemo(() => getHelmetLink(location.query.variant), [location.query.variant])
  const { dispatchApplyPromoCodeAndShowModal, dispatchRedirect, dispatchTrackUTMAndPromoCode } =
    useGetPromoBannerDispatchedActions()

  const canShowPromoBanner = useCanShowPromoBanner()
  const applyDiscount = () => {
    dispatchApplyPromoCodeAndShowModal()
    dispatchRedirect()
    dispatchTrackUTMAndPromoCode()
  }

  const { ctaText, ctaUri } = useGetCtaProps()

  const { defaultBannerText } = usePromoBannerText()
  const isDiscountComprehensionEnabled = useIsDiscountComprehensionEnabled()

  const handleSignupCTAClick = () => {
    datadogRum.addAction('start_onboarding')
    dispatch(homeGetStarted(ctaUri))
  }

  const recipes = useMemo(() => formatRecipes(menuResponse), [menuResponse])

  return (
    <>
      <Helmet
        title={seo.title}
        meta={seo.meta}
        link={link}
        style={[
          {
            cssText: `
              #react-root {
                height: 100%;
              }
            `,
          },
        ]}
      />

      <Homepage
        applyDiscount={applyDiscount}
        isPromoBannerVisible={canShowPromoBanner}
        promoBannerText={defaultBannerText}
        signupCTA={ctaText}
        onSignupCTAClick={handleSignupCTAClick}
        recipes={recipes}
        isDiscountComprehensionEnabled={isDiscountComprehensionEnabled}
      />

      <RibbonTriggerContainer name="home-page" />
    </>
  )
}
